import React, { Fragment, useState, useEffect } from 'react';
import { isStyledComponent } from 'styled-components';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import Drawer from '@material-ui/core/Drawer';
import { withStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';

import { withOffset } from 'Context/LayoutContext';
import { useDisclosure } from 'Util/Hooks';

import { Text, SvgIcon, Box } from '../../Base';

const ANCHOR_TYPES = {
  RIGHT: 'right',
  LEFT: 'left',
};

const styles = {
  paperAnchorBottom: {
    height: 'calc(100% - (100vw * 9 / 16))',
    overflowY: 'hidden',
  },
  paperAnchorRight: {
    width: '375px',
  },
  paperAnchorLeft: {
    width: '375px',
  },
};

const ClassroomDrawerWrapper = ({ children }) => {
  const appElement = document.querySelector('#app');
  return appElement ? ReactDOM.createPortal(children, appElement) : <Fragment>{children}</Fragment>;
};
const ClassroomDrawer = ({
  isOpen,
  toggleComponent,
  onToggleComponentClick,
  closeIconComponent,
  drawerProps,
  classes,
  title,
  children,
  styleWrapperChildrenProps,
}) => {
  const CloseIconComponent =
    isStyledComponent(closeIconComponent) || typeof closeIconComponent === 'function'
      ? closeIconComponent
      : (props) => <SvgIcon icon={SvgIcon.ICON_LIST.ARROW_DOWN} size={24} mb={2} {...props} />;

  const {
    onDrawerOpen,
    onDrawerClose,
    onDrawerToggle,
    horizontalAnchor,
    horizontal,
    ...restDrawerProps
  } = drawerProps || {};

  const [isToggleOpen, onToggleOpen, onToggleClose, onToggleDrawer] = useDisclosure({
    isOpen,
    onOpen: onDrawerOpen,
    onClose: onDrawerClose,
    onToggle: onDrawerToggle,
  });

  const toggleDrawer = () => {
    onToggleDrawer();
    typeof onToggleComponentClick === 'function' && onToggleComponentClick();
  };

  const toggle = toggleComponent && React.cloneElement(toggleComponent, { onClick: toggleDrawer });

  const childrenComp =
    typeof children === 'function'
      ? children(onToggleClose, isToggleOpen)
      : React.cloneElement(children, {
          onCloseDrawer: onToggleClose,
          drawerOpen: isToggleOpen,
        });

  return (
    <Fragment>
      {toggle}
      <ClassroomDrawerWrapper>
        <Box style={{ position: 'absolute' }}>
          <Drawer
            open={isToggleOpen}
            variant={'temporary'}
            anchor={horizontal ? horizontalAnchor : 'bottom'}
            onClose={onToggleClose}
            classes={{
              paperAnchorBottom: classes.paperAnchorBottom,
              paperAnchorRight: classes.paperAnchorRight,
              paperAnchorLeft: classes.paperAnchorLeft,
            }}
            {...restDrawerProps}
          >
            {title && (
              <Box p={4} bg={'white'} mt={'APP_TOP_OFFSET'}>
                <CloseIconComponent
                  data-testid="btn-sidebar-classroom-close"
                  onClick={onToggleClose}
                />
                <Text textStyle={'heading2'}>{title}</Text>
              </Box>
            )}
            <Box height={'100%'} style={{ overflowY: 'auto', ...styleWrapperChildrenProps }}>
              {childrenComp}
            </Box>
          </Drawer>
        </Box>
      </ClassroomDrawerWrapper>
    </Fragment>
  );
};

ClassroomDrawer.ANCHOR_TYPES = ANCHOR_TYPES;

ClassroomDrawer.propTypes = {
  toggleComponent: PropTypes.element,
  onToggleComponentClick: PropTypes.func,
  closeIconComponent: PropTypes.element,
  drawerProps: PropTypes.object,
};

ClassroomDrawer.defaultProps = {
  onToggleComponentClick: () => {},
  drawerProps: {
    onDrawerClose: () => {},
    onDrawerOpen: () => {},
    horizontalAnchor: ANCHOR_TYPES.LEFT,
  },
};

export default compose(withStyles(styles), withOffset)(ClassroomDrawer);
